import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconEdit, IconPlus, IconShieldLock, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeleteRole, useRoles } from './rolesApi';
import { RoleFormDrawer } from './RoleFormDrawer';
import type { RoleDto } from './types';

export function RolesPage() {
  const { t } = useTranslation();
  const { data: roles, isLoading } = useRoles();
  const deleteMutation = useDeleteRole();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<RoleDto | null>(null);
  const [deleting, setDeleting] = useState<RoleDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (role: RoleDto) => {
    setEditing(role);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.roleId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch (err) {
      // Surface "system roles cannot be deleted" from the API instead of a
      // generic toast — the user needs to know WHY this one was blocked.
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: apiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const hasRoles = (roles?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('roles.page.title')}
        subtitle={t('roles.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('roles.page.create')}
          </Button>
        }
      />

      <Paper p={hasRoles ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasRoles ? (
          <EmptyState
            icon={<IconShieldLock size={22} stroke={1.6} />}
            title={t('roles.empty.title')}
            description={t('roles.empty.body')}
            actionLabel={t('roles.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('roles.columns.name')}</Table.Th>
                <Table.Th>{t('roles.columns.code')}</Table.Th>
                <Table.Th>{t('roles.columns.permissions')}</Table.Th>
                <Table.Th>{t('roles.columns.kind')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {roles!.map((role) => (
                <Table.Tr key={role.roleId}>
                  <Table.Td>
                    <Text fw={500} c="var(--wheels-ink-strong)">
                      {role.nameAr}
                    </Text>
                    {role.nameEn && (
                      <Text fz="xs" c="var(--wheels-ink-muted)">
                        {role.nameEn}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {role.code}
                    </span>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray" radius="sm" className="font-numeric">
                      {t('roles.permissionsCount', { count: role.permissionIds.length })}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {role.isSystem ? (
                      <Badge color="wheels" variant="light" radius="sm">
                        {t('roles.system')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('roles.custom')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(role)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          role.isSystem
                            ? t('roles.cannotDeleteSystem')
                            : t('common.delete')
                        }
                        withinPortal
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(role)}
                          aria-label={t('common.delete')}
                          disabled={role.isSystem}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <RoleFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        role={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('roles.delete.title')}
        message={t('roles.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function apiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
